import { useTranslation } from 'react-i18next'

import {
  CalculatorIcon,
  ClipboardTextIcon,
  CoinsIcon,
  FireIcon,
  GlobeHemisphereWestIcon,
  HandCoinsIcon,
  PackageIcon,
  SirenIcon,
} from '@phosphor-icons/react'

import { Alert, CountryFlag, FormatNumber, FormatPrice, Text } from '@reveni-io/reveni-ui'

import { statisticOrientations } from '@/common/components/Statistic/constants'
import Statistic from '@/common/components/Statistic/Statistic'
import StatisticGroup from '@/common/components/StatisticGroup/StatisticGroup'
import { getRegionByCode } from '@/metrics/pages/DashboardPage/ShipmentsMetricsContent/helpers'
import MotionContainer from '@/metrics/pages/DashboardPage/ShipmentsMetricsContent/MotionContainer/MotionContainer'

import StatisticsSkeleton from './StatisticsSkeleton/StatisticsSkeleton'
import * as Styles from './styles'

const Statistics = ({ metrics, loading, regions, currency, infoType, selectedCountry }) => {
  const { t } = useTranslation()

  const topRegion = getRegionByCode(regions, metrics?.top_region)
  const showNoCountryDataError = infoType === 'country' && !metrics?.total_orders_count

  if (showNoCountryDataError)
    return (
      <div>
        <MotionContainer>
          <Alert type="warning">{t('No hay datos para el país seleccionado')}</Alert>
        </MotionContainer>
      </div>
    )

  if (loading) return <StatisticsSkeleton />

  return (
    <MotionContainer>
      <Styles.MainContainer>
        <Text type="titleM">
          {selectedCountry
            ? t('Información de {{country}}', { country: regions.find(r => r.code === selectedCountry)?.name })
            : t('Información global')}
        </Text>
        <Styles.Container>
          <Styles.StatisticsGroup orientation={statisticOrientations.vertical}>
            <Statistic
              size="small"
              isOutlined={false}
              label={t('Pedidos totales')}
              Icon={ClipboardTextIcon}
              value={<FormatNumber style="decimal">{metrics?.total_orders_count}</FormatNumber>}
            />
            <Statistic
              size="small"
              isOutlined={false}
              label={t('Valor total de pedidos')}
              Icon={CoinsIcon}
              value={<FormatPrice currency={currency}>{metrics?.total_order_value}</FormatPrice>}
            />
            <Statistic
              size="small"
              isOutlined={false}
              label={t('Valor medio de pedido')}
              Icon={CoinsIcon}
              value={<FormatPrice currency={currency}>{metrics?.average_order_value}</FormatPrice>}
            />
            <Statistic
              size="small"
              isOutlined={false}
              label={t('Precio total de envíos')}
              Icon={PackageIcon}
              value={<FormatPrice currency={currency}>{metrics?.total_shipping_paid}</FormatPrice>}
            />
            <Statistic
              size="small"
              isOutlined={false}
              label={t('Coste total de aduanas')}
              Icon={SirenIcon}
              value={<FormatPrice currency={currency}>{metrics?.total_customs_paid}</FormatPrice>}
            />
            <Statistic
              size="small"
              isOutlined={false}
              label={t('Total de aduanas y envíos')}
              Icon={CalculatorIcon}
              value={<FormatPrice currency={currency}>{metrics?.total_shipping_and_customs_collected}</FormatPrice>}
            />
            <Statistic
              size="small"
              isOutlined={false}
              label={t('Valor medio de aduanas y envíos')}
              Icon={HandCoinsIcon}
              value={<FormatPrice currency={currency}>{metrics?.average_shipping_and_customs_collected}</FormatPrice>}
            />
          </Styles.StatisticsGroup>
          <Styles.RightStatisticsContainer>
            {topRegion && (
              <StatisticGroup orientation={statisticOrientations.vertical}>
                <Statistic
                  size="small"
                  isOutlined={false}
                  label={t('Región con más pedidos')}
                  Icon={GlobeHemisphereWestIcon}
                  value={
                    <Styles.TopRegionContainer>
                      <CountryFlag code={topRegion?.country?.code} />
                      {topRegion?.province?.name || topRegion?.country?.name}
                    </Styles.TopRegionContainer>
                  }
                />
              </StatisticGroup>
            )}
            <StatisticGroup orientation={statisticOrientations.vertical}>
              <Statistic
                size="small"
                isOutlined={false}
                label={t('Producto más enviado')}
                Icon={FireIcon}
                value={
                  <Styles.TopProductContainer>
                    <img src={metrics?.best_selling_product?.image_url} alt={metrics?.best_selling_product?.title} />
                    {metrics?.best_selling_product?.title}
                  </Styles.TopProductContainer>
                }
              />
              <Statistic
                size="small"
                isOutlined={false}
                label={t('Unidades vendidas')}
                Icon={FireIcon}
                value={<FormatNumber style="decimal">{metrics?.best_selling_product?.total_sold}</FormatNumber>}
              />
            </StatisticGroup>
            <StatisticGroup orientation={statisticOrientations.vertical}>
              <Statistic
                size="small"
                isOutlined={false}
                label={t('Categoría más vendida')}
                Icon={FireIcon}
                value={metrics?.best_selling_category?.title}
              />
              <Statistic
                size="small"
                isOutlined={false}
                label={t('Unidades vendidas')}
                Icon={FireIcon}
                value={<FormatNumber style="decimal">{metrics?.best_selling_category?.total_sold}</FormatNumber>}
              />
            </StatisticGroup>
          </Styles.RightStatisticsContainer>
        </Styles.Container>
      </Styles.MainContainer>
    </MotionContainer>
  )
}

export default Statistics
